import React from 'react';
import { IonButton } from '@ionic/react';
import { BOOT_PHASES, trackBootPhase, type BootPhase } from './boot-phase';
import styles from './AppBootMask.module.css';

const LOGO_URL = `${process.env.PUBLIC_URL || ''}/assets/login/logo_agent.png`;

/** Oltre questa attesa si offre una via d'uscita invece di lasciare girare la barra. */
const ESCAPE_HATCH_DELAY_MS = 8000;

/** Rimuove lo splash statico di `index.html` quando React prende il suo posto. */
function dismissHtmlBootSplash(): void {
  document.getElementById('boot-splash')?.remove();
}

export interface AppBootMaskProps {
  /** Fase dichiarata dal gate che monta la maschera. */
  phase?: BootPhase;
  /** Testo alternativo all'etichetta di fase (es. messaggio di riparazione). */
  message?: string;
  /** Mostra la via di fuga verso il login se l'attesa si prolunga. */
  withEscapeHatch?: boolean;
}

/**
 * Unica schermata di attesa dell'avvio.
 *
 * @remarks
 * Tutti i gate del boot montano questo componente: cambia la fase, non la
 * grafica. La barra e' determinata e non torna mai indietro (vedi
 * `trackBootPhase`), cosi' quattro attese in sequenza si leggono come una sola.
 */
export function AppBootMask({
  phase = 'session',
  message,
  withEscapeHatch = false,
}: AppBootMaskProps): React.ReactElement {
  const [showEscape, setShowEscape] = React.useState(false);

  // La fase effettiva e' la piu' avanzata raggiunta finora, non quella
  // dichiarata: due gate possono sovrapporsi durante lo scambio.
  const effectivePhase = trackBootPhase(phase);
  const { progress, label } = BOOT_PHASES[effectivePhase];
  const isDone = effectivePhase === 'ready';

  React.useEffect(() => {
    dismissHtmlBootSplash();
  }, []);

  React.useEffect(() => {
    if (!withEscapeHatch) return undefined;
    const timer = window.setTimeout(
      () => setShowEscape(true),
      ESCAPE_HATCH_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [withEscapeHatch]);

  const handleGoToLogin = React.useCallback(() => {
    window.location.href = '/login';
  }, []);

  return (
    <div
      className={styles.root}
      role="status"
      aria-live="polite"
      aria-busy={!isDone}
    >
      <img src={LOGO_URL} alt="Agent" className={styles.logo} />

      <div
        className={styles.track}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
      >
        <div
          className={`${styles.fill} ${isDone ? styles.fillDone : ''}`}
          style={{ width: `${progress}%` }}
        />
      </div>

      <p className={styles.label}>{message?.trim() || label}</p>

      {showEscape ? (
        <div className={styles.escape}>
          <p className={styles.escapeText}>
            Sta impiegando più del previsto. Puoi tornare al login e riprovare.
          </p>
          <IonButton
            fill="outline"
            size="small"
            className={styles.escapeButton}
            onClick={handleGoToLogin}
          >
            Vai al login
          </IonButton>
        </div>
      ) : null}

      <p className={styles.signature}>by ILP Consulting</p>
    </div>
  );
}
